"use client"

import FormBooking from "@/components/tours/form-booking"
import ModalImageGallery from "@/components/tours/modal-image-gallery"
import TabContent from "@/components/tours/tab-content"
import TourCard from "@/components/tours/tour-card"
import { Images } from "@/constants/images"
import { t } from "@/languages/i18n"
import { formatPrice } from "@/lib/price-helper"
import { parseElevation, toNFC } from "@/lib/string-helper"
import { updateImageSrc } from "@/lib/utils"
import { Image as ImageType, TourDetail, TourDetailData } from "@/types/tour"
import Image from "next/image"
import Link from "next/link"
import { useEffect, useState } from "react"
import { fetchToursV2 } from "../../(admin)/modules/tour/services/tours"
import NotFound from "../../app/[locale]/not-found"
import BreadCrumb, { BreadCrumbItem } from "../breadcrumb"

const TourDetailPage = ({ data, locale }: { data?: TourDetail; locale?: string }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isModalImageGalleryOpen, setIsModalImageGalleryOpen] = useState(false)
  const [clickedImage, setClickedImage] = useState<ImageType | string | undefined>()
  const [fallbackRelatedTours, setFallbackRelatedTours] = useState<any[]>([])
  if (!data) return <NotFound />
  console.log(data)
  const categoryTour = data?.category?.data?.attributes
  const slugCategory = categoryTour?.slug
    ? locale === "en"
      ? `/${categoryTour?.slug}`
      : `/${categoryTour?.slug}-vi`
    : "/tours"

  const itemBreadCrumb: BreadCrumbItem[] = [
    {
      title: categoryTour?.name ?? "Tours",
      link: slugCategory,
    },
    {
      title: data.name ?? "",
      link: "",
    },
  ]

  const tourInfo = [
    {
      label: t("Duration"),
      icon: Images.time,
      value: data.duration,
    },
    {
      label: t("Location"),
      icon: Images.locactionSummary,
      value: data.location,
    },
    {
      label: t("Tags"),
      icon: Images.tag,
      value: data.tags?.data?.map(item => item.attributes?.name).join(", "),
    },
  ].filter(item => item.value && item.value !== "")

  const tourSummary = [
    {
      label: t("Distance"),
      icon: Images.grayDistance,
      value: data.distance,
    },
    {
      label: t("Difficulty"),
      icon: Images.speedLight,
      value: data.difficulty,
    },
    {
      label: t("Elevation"),
      icon: Images.mountain,
      value: parseElevation(data.elevation),
    },
  ].filter(item => !item.value || item.value !== "")

  const handleBookNow = () => {
    setIsModalOpen(true)
  }

  const handleClickImage = (image?: ImageType | string) => {
    if (!image) return

    setClickedImage(typeof image === "string" ? image : (image as ImageType))
    setIsModalImageGalleryOpen(true)
  }

  const handleViewGallery = () => {
    setClickedImage(undefined)
    setIsModalImageGalleryOpen(true)
  }

  const loadFallback = async () => {
    try {
      // Lấy danh sách destination names từ tour hiện tại
      const currentTourDestinations =
        (data as any)?.destinations?.data?.map((dest: any) => dest?.attributes?.name).filter(Boolean) || []

      const res = await fetchToursV2({}, 20, "createdAt:DESC")
      let filteredPosts = data?.slug
        ? res.data.filter((post: TourDetailData) => {
            return post.attributes?.slug !== data.slug
          })
        : res.data

      // Ưu tiên các tour có destinations trùng với tour hiện tại
      if (currentTourDestinations.length > 0) {
        const toursWithDestinations: TourDetailData[] = []
        const toursWithoutDestinations: TourDetailData[] = []

        filteredPosts.forEach((post: TourDetailData) => {
          const postDestinations =
            (post.attributes as any)?.destinations?.data?.map((dest: any) => dest?.attributes?.name).filter(Boolean) ||
            []

          // Kiểm tra xem tour có destinations trùng với tour hiện tại không
          const hasMatchingDestination = postDestinations.some((destName: string) =>
            currentTourDestinations.includes(destName),
          )

          if (hasMatchingDestination) {
            toursWithDestinations.push(post)
          } else {
            toursWithoutDestinations.push(post)
          }
        })

        // Sắp xếp: tours có destinations trùng trước, sau đó là các tour khác
        filteredPosts = [...toursWithDestinations, ...toursWithoutDestinations]
      }

      setFallbackRelatedTours(filteredPosts.slice(0, 3))
    } catch (err) {
      // ignore silently
    }
  }

  useEffect(() => {
    loadFallback()
  }, [])

  return (
    <div>
      <BreadCrumb items={itemBreadCrumb} />
      <div className="max-w-screen-lg mx-auto flex flex-col px-4 lg:px-0">
        <div className="flex flex-col gap-10 w-full lg:flex-row lg:gap-12">
          <div className="w-full flex flex-col gap-5 sm:gap-10">
            <h1 className="text-4.5xl font-bold text-[#242F3E] leading-[3.375rem]">{data.name ?? ""}</h1>

            <div className="flex flex-row gap-4 flex-wrap">
              {tourInfo.map(item => (
                <div key={item.label} className="flex flex-row gap-2 items-center">
                  <Image src={item.icon} alt={item.label ?? ""} width={22} height={22} sizes="80vw" />

                  <p className="text-base leading-5 text-[#4F4F4F] font-medium">{item.value}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-3 lg:flex-row">
              <div className="relative aspect-square w-full rounded-xl overflow-hidden lg:min-w-[25.9375rem] lg:aspect-[1.004844] cursor-pointer">
                <Image
                  src={updateImageSrc(
                    typeof data.feature_img?.[0] === "string"
                      ? data.feature_img?.[0]
                      : data.feature_img?.[0]?.url ?? "",
                  )}
                  alt={
                    typeof data.feature_img?.[0] === "object"
                      ? (data.feature_img?.[0] as { alternativeText?: string }).alternativeText ?? data.name ?? ""
                      : data.name ?? ""
                  }
                  fill
                  sizes="80vw"
                  className="object-cover"
                  onClick={() =>
                    handleClickImage(
                      typeof data.feature_img?.[0] === "object"
                        ? (data.feature_img?.[0] as { url?: string; alternativeText?: string; name?: string })
                        : data.feature_img?.[0],
                    )
                  }
                />
              </div>

              <div className="flex flex-row gap-3 w-full lg:flex-col relative">
                {data.feature_img?.slice(1, 3).map((item, index) => (
                  <div
                    key={index}
                    className="relative aspect-square w-full rounded-xl overflow-hidden lg:aspect-[1.345] cursor-pointer"
                  >
                    <Image
                      src={updateImageSrc(typeof item === "string" ? item : item?.url ?? "")}
                      alt={
                        typeof item === "object"
                          ? (item as { alternativeText?: string }).alternativeText ?? data.name ?? ""
                          : data.name ?? ""
                      }
                      fill
                      sizes="80vw"
                      className="object-cover"
                      onClick={() => handleClickImage(typeof item === "object" ? (item as ImageType) : item)}
                    />
                  </div>
                ))}

                {data.feature_img?.length && data.feature_img?.length > 3 && (
                  <div
                    className="absolute bottom-2.5 right-2.5 bg-[#242F3EB2] rounded-xl py-1.5 px-3 flex flex-row gap-2 items-center cursor-pointer hover:bg-[#0B9444] transition-all duration-200"
                    onClick={handleViewGallery}
                  >
                    <Image src={Images.photoIcon} alt="photo" width={22} height={22} sizes="80vw" />

                    <p className="text-xs leading-[0.9375rem] text-white">{t("View Photos")}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="w-full flex flex-col gap-6 lg:max-w-[28.5rem]">
            <div className="p-6 rounded-xl border border-[#6670851F] relative overflow-hidden">
              {data.is_most_popular && (
                <div className="absolute top-0 right-0 p-2 bg-[#C9E4A5]">
                  <p className="text-[0.625rem] leading-3 font-semibold text-[#0B9444] lg:text-xs lg:leading-3">
                    {t("Most Popular")}
                  </p>
                </div>
              )}

              <div className="flex flex-col gap-2">
                <h2 className="text-[1.625rem] leading-[2.125rem] font-bold text-[#242F3E] sm:leading-[2.25rem] sm:text-[1.75rem]">
                  {t("Tour Summary")}
                </h2>

                {data?.description && (
                  <h3 className="text-base leading-[1.6875rem] text-[#242F3E] font-medium lg:text-lg lg:leading-5.5">
                    {data?.description}
                  </h3>
                )}
              </div>

              {tourSummary.length > 0 && (
                <div className="flex flex-row flex-wrap gap-5 pt-10">
                  {tourSummary.map(
                    item =>
                      item.value && (
                        <div key={item.label} className="flex flex-row gap-2 items-start ">
                          <Image src={item.icon} alt={item.label ?? ""} width={20} height={20} sizes="80vw" />

                          <div className="flex flex-col gap-2">
                            <p className="text-sm leading-[1.0625rem] text-[#242F3E] font-semibold uppercase">
                              {item.label}
                            </p>

                            <div
                              className="text-base leading-[1.6875rem] text-[#242F3E] font-normal"
                              dangerouslySetInnerHTML={{ __html: toNFC(item.value) }}
                            />
                          </div>
                        </div>
                      ),
                  )}
                </div>
              )}

              <div className="pt-6 lg:pt-2">
                <p className="text-base font-normal leading-5 text-[#242F3E] flex items-center gap-1 flex-wrap">
                  {t("From")}
                  <span className="font-semibold text-3.5xl leading-[2.4375rem] text-[#0B9444]">
                    {formatPrice(data?.price_overview, locale)}
                  </span>
                  / {t("person")}
                </p>

                {data?.price_note && (
                  <p className="text-base leading-5 font-normal italic text-[#242F3E]">{data?.price_note}</p>
                )}
              </div>

              <div
                onClick={handleBookNow}
                className="w-full mt-6 font-bold text-base leading-5 text-white bg-[#0B9444] rounded-full py-4 capitalize text-center transition-all duration-200 cursor-pointer border border-[#0B9444] hover:bg-white hover:text-[#0B9444]"
              >
                {t("Book your trip now")}
              </div>
            </div>

            {data?.inclusions && data?.inclusions?.length > 0 && (
              <div className="grid grid-cols-2 gap-5 lg:gap-y-6">
                {data.inclusions.map(item => (
                  <div key={item?.id} className="flex flex-col lg:gap-2">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:gap-1.5">
                      {item?.icon && (
                        <Image
                          src={Images[item?.icon as keyof typeof Images]}
                          alt={item?.label ?? ""}
                          width={24}
                          height={24}
                          sizes="80vw"
                          objectFit="contain"
                          className="pb-1 w-6 h-6"
                        />
                      )}

                      <p className="text-base leading-[1.6875rem] font-semibold text-[#242F3E]">{item?.label}</p>
                    </div>
                    <p className="text-sm leading-[1.0625rem] font-normal text-[#242F3E]">{item?.description}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <TabContent data={data} />

        <h2 className="text-base leading-[1.6875rem] font-medium text-[#242F3E] pb-4">
          {t("Need more info? Check our")}{" "}
          <Link
            href="/faqs"
            className="text-blue-500 font-semibold hover:text-[#0B9444] hover:underline transition-all duration-200"
          >
            FAQs{" "}
          </Link>
          {t("page for answers to the most common questions about our tours, booking, and travel details.")}
        </h2>
      </div>

      {fallbackRelatedTours && fallbackRelatedTours?.length > 0 && (
        <div className="bg-[#F9F9FB]">
          <div className="pb-10 max-w-screen-lg mx-auto px-4 py-10 lg:px-0 lg:py-[7.5rem]">
            <h2 className="text-4.5xl leading-[3.375rem] font-bold text-[#242F3E] pb-6 lg:pb-10">
              {t("Other suggestions for you")}{" "}
            </h2>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-10">
              {fallbackRelatedTours?.map(item => (
                <TourCard key={item?.id} tourDetail={item?.attributes ?? {}} />
              ))}
            </div>
          </div>
        </div>
      )}

      <FormBooking isModalOpen={isModalOpen} currentTour={data} setIsModalOpen={setIsModalOpen} locale={locale} />

      <ModalImageGallery
        images={
          data?.feature_img && data?.feature_img?.length > 0
            ? data.feature_img
            : data?.feature_img?.filter((item): item is ImageType => typeof item !== "string")
        }
        isOpen={isModalImageGalleryOpen}
        setIsOpen={setIsModalImageGalleryOpen}
        clickedImage={clickedImage}
      />
    </div>
  )
}

export default TourDetailPage
